import type { FC } from "react";
import { useEffect, useMemo, useState, type JSX } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { LegendWithOneLineBulletFlex } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { ICategory } from "app/common/services/categoryService.types";
import { i18nFilter } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";

import { getSeries, getChartConfigSingleMode } from "./AllTrafficChannelsOverviewBarChartUtils";
import { ChannelsOverviewBarChart } from "./ChannelsOverviewBarChart";
import { ChannelsOverviewBarChartCategoryBenchmark } from "./ChannelsOverviewBarChartCategoryBenchmark";
import type {
    DataFormat } from "./ChannelsOverviewBarChartContainerFormat";
import {
    numbersDataFormat,
    percentDataFormat,
    dataModeMap,
} from "./ChannelsOverviewBarChartContainerFormat";
import { LegendContainerAllTraffic, AllTrafficContainerSingleMode, LegendTotalAllTraffic } from "./StyledComponents";
import type { TrafficDataByDeviceType } from "./types";

interface IAllTrafficChannelsOverviewBarChartSingleMode {
    isCategoryBenchmarkVisible: boolean;
    data: TrafficDataByDeviceType;
    dataFormat: DataFormat;
    fromDate: unknown;
    isWWO: boolean;
    onCategorySelected: (category: ICategory | null) => void;
}

export const AllTrafficChannelsOverviewBarChartSingleMode: FC<IAllTrafficChannelsOverviewBarChartSingleMode> = ({
    isCategoryBenchmarkVisible,
    data,
    dataFormat,
    fromDate,
    isWWO,
    onCategorySelected,
}) => {
    const i18n = i18nFilter();
    const { params } = useCurrentStateAndParams();
    const { key, category, duration } = params;
    const isCompare = key.split(",").length > 1;
    const [seriesSingleMode, setSeriesSingleMode] = useState(null);
    const [seriesForLegendSingleMode, setSeriesForLegendSingleMode] = useState(null);

    const configSingleMode = useMemo(
        () =>
            data &&
            getChartConfigSingleMode({
                data,
                dataFormat,
                isCompare,
                fromDate,
            }),
        [data, fromDate, isCompare, dataFormat],
    );

    useEffect(() => {
        if (!data) {
            return;
        }
        const dataForSelectedVisualization = getSeries(data, dataFormat, fromDate);
        if (dataFormat === percentDataFormat) {
            const dataForAbsoluteNumbers = getSeries(data, numbersDataFormat, fromDate);

            setSeriesSingleMode(dataForSelectedVisualization);
            setSeriesForLegendSingleMode(dataForAbsoluteNumbers);
        } else {
            setSeriesSingleMode(dataForSelectedVisualization);
            setSeriesForLegendSingleMode(dataForSelectedVisualization);
        }
    }, [dataFormat, data]);

    const calcTotalDataFromLegendObj = (data) => {
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
            sum += data[i];
        }
        return sum;
    };
    const calcTotalTraffic = (legendsData) => {
        let sum = 0;
        legendsData?.map((legend) => {
            sum += calcTotalDataFromLegendObj(legend.data);
        });
        return sum;
    };

    const getLegendsAllTrafficSingleMode = (legendsData): JSX.Element[] => {
        return legendsData?.map((legendObj) => {
            const value = minAbbrNumberFilter()(calcTotalDataFromLegendObj(legendObj?.data)).toString();
            return (
                <LegendWithOneLineBulletFlex
                    key={legendObj?.id}
                    text={legendObj?.name}
                    isChecked
                    size={1}
                    labelColor={legendObj?.color}
                    value={Number(value) === 0 ? "N/A" : value}
                />
            );
        });
    };

    return (
        <AllTrafficContainerSingleMode isWWO={isWWO}>
            <ChannelsOverviewBarChart
                data={seriesSingleMode}
                config={configSingleMode}
                dataAutomation="ChannelsOverviewBarChartAllTraffic"
            />
            {!isWWO ? (
                <Flex
                    flexDirection="column"
                    margin="10px 0 24px 0"
                    gap="16px"
                >
                    {isCategoryBenchmarkVisible ? (
                        <ChannelsOverviewBarChartCategoryBenchmark
                            isUnbound={false}
                            categoryParam={category}
                            dataMode={dataModeMap[dataFormat]}
                            durationParam={duration}
                            onCategorySelected={onCategorySelected}
                        />
                    ) : null}
                    <LegendContainerAllTraffic>
                        {getLegendsAllTrafficSingleMode(seriesForLegendSingleMode)}
                        <LegendTotalAllTraffic>
                            <span>{i18n("mmx.channelsoverview.bar.chart.legend.single.TotalTraffic")}</span>
                            <span>{minAbbrNumberFilter()(calcTotalTraffic(seriesForLegendSingleMode))}</span>
                        </LegendTotalAllTraffic>
                    </LegendContainerAllTraffic>
                </Flex>
            ) : null}
        </AllTrafficContainerSingleMode>
    );
};
